import { getPricingPageProductRoute } from 'pricing-pages-lib/utils/urls';
import { PurchaseMotionCTA } from 'purchase-motions-components/components/PurchaseMotionCTA/PurchaseMotionCTA';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useEnrollPortalMutation } from '../../../../__generated__/chirp/com/hubspot/fintech/discounts/rpc/PromotionsRpc.hooks';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { usePurchaseMotionButtonProductConfigurations } from '../../../hooks/usePurchaseMotionButtonProductConfigurations';
import { useProductConfigurationsToPurchaseRequest } from '../../../hooks/usePurchaseOrderGeneration';
import { useTrackingFlow } from '../../../hooks/useTrackingFlow';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { productsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { trackingProductNameSelector } from '../../../selectors/TrackingSelectors';
import { PROMOTION_MAPPING, TRIAL_PROMO_LENGTH, logHC0136Exposure, useHC0136ExperimentEffect } from '../../../utils/experiments/hc0136';
import { Metrics } from '../../../utils/metrics';
import { usePurchaseMotionCTAText } from '../../../v2/hooks/purchasing/usePurchaseMotionCTAText';
import { trackButtonClick, trackBuyButtonClick, trackTalkToSalesClick } from '../../../v2/utils/tracking/trackingEventUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const PurchaseMotionButtonWrapper = props => {
  var _props$productConfigu;
  const {
    apiName,
    nonMerchandiseIdIdentifier,
    queryParamTerm,
    onClick,
    skipInterstitialPage,
    trackingSource,
    purchaseMotionLocation,
    purchaseMotion,
    redirectQueryParams
  } = props;
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const productConfigurations = usePurchaseMotionButtonProductConfigurations((_props$productConfigu = props.productConfigurations) !== null && _props$productConfigu !== void 0 ? _props$productConfigu : [], String(purchaseMotion));
  const purchaseRequest = useProductConfigurationsToPurchaseRequest(productConfigurations, displayedCurrencyCode, queryParamTerm);
  const {
    isHC0136Variation,
    isEligibleForHC0136
  } = useHC0136ExperimentEffect(apiName);
  const [enrollPortalForPromotion] = useEnrollPortalMutation({
    ignoreResults: true
  });
  const isAppAuth = useAppSelector(state => isAppAuthSelector(state));
  const productIdentifier = nonMerchandiseIdIdentifier || apiName;
  const trackingProductNames = useSelector(state => productConfigurations.map(({
    skuId
  }) => trackingProductNameSelector(state, {
    skuId
  })));
  const ownedProducts = useSelector(state => productsFromProductInventorySelector(state)).map(p => p.apiName);
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  const handleClick = () => {
    onClick === null || onClick === void 0 || onClick();
    switch (purchaseMotion) {
      case 'BUY_TOUCHLESS':
        Metrics.counter('buy-now-clicked', {
          source: props.trackingData.uniqueId,
          version: 'v1'
        }).increment();
        trackBuyButtonClick({
          purchaseMotion,
          activeCurrencyCode: displayedCurrencyCode,
          productNames: trackingProductNames,
          ownedProducts,
          trackingSource: props.trackingSource,
          screen: props.screen,
          flow
        });
        break;
      case 'BUY_ADDITIONAL_SEATS':
      case 'BUY_ADDITIONAL_CONTACTS':
        if (purchaseMotionLocation === 'PRICING_PAGE_INTERSTITIAL') {
          return;
        }
        trackBuyButtonClick({
          purchaseMotion,
          activeCurrencyCode: displayedCurrencyCode,
          productNames: trackingProductNames,
          ownedProducts,
          trackingSource: props.trackingSource,
          screen: props.screen,
          flow
        });
        break;
      case 'TALK_TO_SALES':
      case 'TALK_TO_SALES_FALLBACK':
        trackTalkToSalesClick({
          flow,
          activeCurrencyCode: displayedCurrencyCode,
          productNames: trackingProductNames,
          ownedProducts,
          trackingSource: props.trackingSource,
          screen: props.screen
        });
        break;
      default:
    }
  };

  // handle all button text overrides
  const buttonText = usePurchaseMotionCTAText({
    purchaseMotion,
    purchaseMotionLocation,
    children: props.children
  });
  const pricingPageRoute = getPricingPageProductRoute(isAppAuth);
  const onTrialStartSuccess = useCallback(() => {
    if (isEligibleForHC0136) {
      logHC0136Exposure();
      if (isHC0136Variation) {
        const promotionId = PROMOTION_MAPPING[apiName];
        const expiresAt = Date.now() + TRIAL_PROMO_LENGTH;
        trackButtonClick({
          action: 'click button',
          value: 'trial promotion added'
        });
        void enrollPortalForPromotion({
          variables: {
            expiresAt,
            promotionId
          }
        });
      }
    }
  }, [apiName, enrollPortalForPromotion, isEligibleForHC0136, isHC0136Variation]);
  return /*#__PURE__*/_jsx(PurchaseMotionCTA, {
    apiName: apiName,
    location: purchaseMotionLocation,
    trackingData: props.trackingData,
    uiButtonProps: Object.assign({}, undefined !== props.block && {
      block: props.block
    }, undefined !== props.use && {
      use: props.use
    }, undefined !== props.size && {
      size: props.size
    }, undefined !== props.disabled && {
      disabled: props.disabled
    }),
    "data-selenium-id": `${trackingSource}-${productIdentifier}-${String(purchaseMotion)}-button`,
    purchaseMotion: purchaseMotion,
    onClick: handleClick,
    ctaProps: {
      purchaseRequest,
      redirectQueryParams,
      shouldDisplayAlertOnFailure: true,
      skipInterstitial: skipInterstitialPage,
      interstitialQueryParams: {
        pricingPageProductRoute: pricingPageRoute || ''
      },
      onTrialStartSuccess
    },
    children: buttonText
  });
};
export default PurchaseMotionButtonWrapper;